import { Collection, IModel, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { IntegrationRuntime } from '../integrationRuntime/integrationRuntime';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { Parameter } from '../base/parameter';
import { IKeyValuePair } from '../../interfaces/IKeyValuePair';
import { IParameterizable } from '../../interfaces/IParameterizable';
import { TaggableNamed } from '../base/taggable-named';
import { OwnedSet } from '../base/ownedset';
import { SharedModel as Model } from '../sharedmodel';
import { ActionConstants } from '../../constants/action-constants';
import { Named, Owned } from '../base/named';
import { GlobalVariables } from '../../constants/globals';
import { type ConnectionKind } from '../../constants/linkedservice-constants';
import { ModelConstants } from '../model-constants';
import { Constants } from '../../shared/shared-constants';
import { sanitizeAndFormatString } from '../../shared/utils/string.util';
import { FabricGatewayType, IMemberGateways } from '../../interfaces/ITridentResource';
import { ParameterConfig } from '../pipeline/parameter-config';
import type { TridentArtifactTypeV2 } from '@trident/data-access-artifact';
import { ConnectionAuthKind, ConnectionAuthKindDisplayNameMap } from '../../interfaces/IAuthenticationType';
import { compact } from 'lodash';
import { Dataset } from '../dataset/dataset';

@Metamodel.model({
    name: ModelConstants.linkedService,
    display: [shellStringResources.ModelName_LinkedService, shellStringResources.ModelName_LinkedServices],
    referable: true,
    reusable: true,
    abstract: false,
    tags: {
        // do not validate linked service which it is referenced by other entity
        noValidationWhenReferenced: () => false,
        // whether the linked service is referenced by other entity or not, do not validate the linked service.
        noValidation: () => false
    },
    constraints: {
        name: {
            required: true,
            pattern: !GlobalVariables.isTrident ? new RegExp('^[^-\\s][^\\\\/:*?"<>|]{1,140}$') : undefined
        }
    },
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.linkedServicesWrite
        },
        deleteResource: {
            action: ActionConstants.linkedServicesDelete
        }
    }
})
export abstract class LinkedService extends TaggableNamed implements IParameterizable {
    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        reference: true,
        type: IntegrationRuntime.MODEL,
        display: shellStringResources.IntegrationRuntime
    })
    public integrationRuntime?: IntegrationRuntime;

    /**
     * encryptedCredential should be set to undefined if user changes anything related to credential, so that the adapter will try to regenerate it
     * Empty string '' means that it has just been converted from Resource to Model and there's no secret in it.
     */
    public encryptedCredential?: string;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public credential?: any;

    /**
     * Resource related to the encryptedCredential, not the resource in the backend
     * Cache it so that there will be no need in Madrid code to handle:
     *   1. Removing secrets in plain text connection string
     *   2. Removing whole secure string property for non-connection-string LinkedService
     * Can/must be set when the encryptedCredential got changed to non-empty value
     * Currently 3 places
     *   1. LinkedServiceAdapter._toModel
     *     Called when loading from the backend resource, encryptedCredential/resource from backend
     *   2. LinkedServiceAdapter.connectVia
     *     Called when toResource is called, encryptedCredential/resource from self-hosted IR
     *   3. LinkedServiceConfigurationComponent.onOkClickAsync
     *     encryptedCredential/resource from backend
     */
    public cachedResource?: Model.LinkedServiceResource;

    /** True when a newer version of this connector exists (e.g. V1 → V2). The old version still works but users should migrate. */
    public isLegacyConnector?: boolean = false;

    public parameterized?: boolean = false;

    public fullyByJsonEditor?: boolean = false;

    // #region Fabric-only properties
    public tridentArtifactType?: TridentArtifactTypeV2;

    // #region DMTS conenction
    public tridentConnectionKind?: ConnectionKind;

    public tridentConnectionAuthKind?: ConnectionAuthKind;

    public fabricGatewayType?: FabricGatewayType;

    public fabricGatewayName?: string;

    public memberGateways?: IMemberGateways[];

    public allowDatasourceThroughOnPremsGateway?: boolean;

    /**
     * Whether the ls model references to an on-premise gateway
     */
    public get isOPDGReferenced(): boolean {
        return this.fabricGatewayType === FabricGatewayType.Enterprise;
    }

    public get isVnetReferenced(): boolean {
        return this.fabricGatewayType === FabricGatewayType.VirtualNetwork;
    }

    /**
     * Specify whether the ls model is created for an external DMTS connection
     */
    public get isExternalConnection(): boolean {
        return this.tridentArtifactType === 'Connection';
    }

    // #endregion

    /**
     * Whether the ls model is the artifact in Fabric workspace
     */
    public get isWorkspaceBuiltIn(): boolean {
        return (
            this.tridentArtifactType === ModelConstants.lakehouseArtifactType ||
            this.tridentArtifactType === ModelConstants.kustoDatabaseArtifactType ||
            this.tridentArtifactType === ModelConstants.dataWarehouseArtifactType ||
            this.tridentArtifactType === ModelConstants.fabricSqlArtifactType ||
            this.tridentArtifactType === ModelConstants.mountedRelationalDatabaseArtifactType ||
            this.tridentArtifactType === ModelConstants.sqlAnalyticsEndpointArtifactType ||
            this.tridentArtifactType === ModelConstants.eventStreamArtifactType
        );
    }

    /**
     * Whether the ls model is created for sample dataset reference
     */
    public get isSampleDatasetConnection(): boolean {
        return this.tridentArtifactType === 'SampleDataset';
    }

    /**
     * Whether the ls model is the workspace built-in artifact in Fabric workspace, sample dataset or external DMTS connection
     */
    public get isTridentLinkedService(): boolean {
        return this.tridentArtifactType != null;
    }

    // #endregion

    /**
     * If fully by json editor, store the references here, so that the debug run can use Walker to find out the referenced linked services
     */
    @Metamodel.attribute({
        type: LinkedService.MODEL,
        collection: Collection.Array,
        reference: true
    })
    public referencesWhenFullyByJsonEditor: LinkedService[] = [];

    @Metamodel.attribute({
        type: Parameter.MODEL,
        collection: Collection.Set,
        constraint: {
            unique: true,
            validator: LinkedService.validateParameters
        }
    })
    public readonly parameters = OwnedSet.create<Parameter>(this);

    /**
     * Currently, only used by copy wizard for inline linked service
     */
    public notPublished?: boolean = false;

    /**
     * Connection string type linked service can contain addiontial fields
     */
    public additionalFields?: Array<IKeyValuePair<string, string | null>> = [];

    public parameterizableAdditionalFields: Array<ParameterConfig<string>>;

    // eslint-disable-next-line
    constructor(name?: string, description?: string) {
        super(name, description);
        this.raw = `{
            "properties": {
                "typeProperties": {
                }
            }
        }`;
    }

    public static validateParameters(linkedservice: LinkedService): IViolation[] | undefined {
        const violations: IViolation[] = [];
        linkedservice.parameters.forEach((parameter) => {
            if (
                GlobalVariables.strictParamValidation &&
                !Constants.alphaNumericUnderscoreSpaceValidation.test(parameter.name)
            ) {
                violations.push({
                    context: parameter,
                    message: () =>
                        sanitizeAndFormatString(
                            shellStringResources.Validator_NamePattern_All_AlphanumericUnderscoreSpace,
                            parameter.name
                        )
                });
            }
        });
        return violations;
    }

    public setCachedResource(
        encryptedCredential: string | undefined,
        cachedResource: Model.LinkedServiceResource | undefined
    ): void {
        // If cachedResource is undefined, always set the encryptedCredential to undefined
        // Otherwise, always set some value to the encryptedCredential
        this.encryptedCredential = cachedResource != null ? encryptedCredential || '' : undefined;
        this.cachedResource = cachedResource != null ? JSON.parse(JSON.stringify(cachedResource)) : undefined;
    }

    public getCachedResourceCopy(): Model.LinkedServiceResource | undefined {
        return this.cachedResource != null ? JSON.parse(JSON.stringify(this.cachedResource)) : undefined;
    }

    /**
     * The function to generate the customized error message when editing an existing linked service
     * e.g. API version in Salesforce linked service cannot be updated as undefined, and we should toast a piece of meaningful error message during editing
     * Note this cannot be integrated into control validator since it doesn't recognize other component's changes
     */
    public editingErrorMessageCustomizer(): string {
        return '';
    }

    public getLinkedServiceType(): string {
        const model = Metamodel.modelOf(this);

        return !!model ? model.name : '';
    }

    /**
     * @return An array of connection details, each containing a display name, value, and property name.
     */
    public getDetails(dataset: Dataset, additionalSkipAttributes?: Set<string>):
        | Array<{
              label?: string;
              value: string | number;
              name: string;
          }>
        | undefined {
        const credentialType: ConnectionAuthKind = this.extension._credentialType$ as ConnectionAuthKind;
        const credentialTypeDetail: {
            label?: string;
            value: string | number;
            name: string;
        } = credentialType
            ? {
                label: shellStringResources.ModelName_AuthenticationKind,
                  value: ConnectionAuthKindDisplayNameMap.has(credentialType)
                      ? ConnectionAuthKindDisplayNameMap.get(credentialType)
                      : credentialType,
                  name: 'credentialType'
              }
            : undefined;
        if (this.fullyByJsonEditor) {
            if (GlobalVariables.isTrident) {
                // If the linked service is fully by json editor, we should get detail from datasourceReference
                try {
                    const path = JSON.parse(JSON.parse(this.raw)?.properties?.datasourceReference).path;
                    if (path) {
                        if (isJSONString(path)) {
                            // If the path is a JSON string, parse it and return the key-value pairs
                            return Object.entries(JSON.parse(path)).reduce((previousValue, currentValue) => {
                                const [key, value] = currentValue;
                                if (value == null || value === '') {
                                    return previousValue;
                                }
                                return previousValue.concat({
                                    value: String(value),
                                    name: key
                                });
                            }, []);
                        } else {
                            return compact([
                                {
                                    label: shellStringResources.ModelName_DataSourcePath,
                                    value: path,
                                    name: 'path'
                                },
                                credentialTypeDetail
                            ]);
                        }
                    }
                } catch {
                    // ignore
                }
            }
            return undefined;
        }

        const model = Metamodel.modelOf(this);
        const attributes = model?.attributes || [];
        const skipAttributes = new Set([
            'name',
            'referencesWhenFullyByJsonEditor',
            'parameters',
            'password',
            'objectId',
            'raw',
            'authenticationType',
            '_id',
            '_owner',
            '_extension'
        ]);
        additionalSkipAttributes?.forEach((attr) => skipAttributes.add(attr));

        return attributes
            .reduce((previousValue, currentValue) => {
                if (skipAttributes.has(currentValue.name)) {
                    return previousValue;
                }
                const value = currentValue.value(this);
                if (value == null || value == '') {
                    return previousValue;
                }
                return previousValue.concat({
                    label: currentValue.display,
                    value: value instanceof Named ? value.name : String(value),
                    name: currentValue.name
                });
            }, [])
            .concat(credentialTypeDetail ? [credentialTypeDetail] : []);
    }
}

const isJSONString = (str: string): boolean => {
    try {
        JSON.parse(str);
        return true;
    } catch {
        return false;
    }
};

export function createLinkedService(name: string, lsType: string, owner: Owned): LinkedService {
    const model = Metamodel.modelByName(lsType);

    if (!!model) {
        const obj = Owned.create(model, owner) as LinkedService;
        if (obj instanceof LinkedService) {
            obj.name = name;

            return obj;
        }
    }

    throw new Error(`Failed to create linked service instance. Invalid linked service type: ` + lsType);
}
